import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import * as RatiosActions from './ratios.actions';
import { RatiosService } from '../../services/ratios.service';
import { selectSelectedStock } from '../stocks/stocks.selectors';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'root',
})
export class RatiosEffects {
  loadRatios$: any = {};
  loadRatio$: any = {};
  createRatio$: any = {};
  updateRatio$: any = {};
  deleteRatio$: any = {};
  ratioSuccess$: any = {};

  constructor(
    private actions$: Actions,
    private ratiosService: RatiosService,
    private router: Router,
    private store: Store
  ) {
    this.loadRatios$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RatiosActions.loadRatios),
        switchMap(({ stockId, filters }) =>
          this.ratiosService.getRatios(stockId, filters).pipe(
            map((response) => {
              const ratios = response.data.items || [];
              return RatiosActions.loadRatiosSuccess({ ratios });
            }),
            catchError((error) => of(RatiosActions.loadRatiosFailure({ error: error.message })))
          )
        )
      )
    );

    this.loadRatio$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RatiosActions.loadRatio),
        switchMap(({ id }) =>
          this.ratiosService.getRatio(id).pipe(
            map((response) => {
              const ratio = response.data || response;
              return RatiosActions.loadRatioSuccess({ ratio });
            }),
            catchError((error) => of(RatiosActions.loadRatioFailure({ error: error.message })))
          )
        )
      )
    );

    this.createRatio$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RatiosActions.createRatio),
        switchMap(({ ratio }) =>
          this.ratiosService.createRatio(ratio).pipe(
            map((response) => {
              const createdRatio = response.data || response;
              return RatiosActions.createRatioSuccess({ ratio: createdRatio });
            }),
            catchError((error) => of(RatiosActions.createRatioFailure({ error: error.message })))
          )
        )
      )
    );

    this.updateRatio$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RatiosActions.updateRatio),
        switchMap(({ id, ratio }) =>
          this.ratiosService.updateRatio(id, ratio).pipe(
            map((response) => {
              const updatedRatio = response.data || response;
              return RatiosActions.updateRatioSuccess({ ratio: updatedRatio });
            }),
            catchError((error) => of(RatiosActions.updateRatioFailure({ error: error.message })))
          )
        )
      )
    );

    this.deleteRatio$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RatiosActions.deleteRatio),
        switchMap(({ id }) =>
          this.ratiosService.deleteRatio(id).pipe(
            map(() => RatiosActions.deleteRatioSuccess({ id })),
            catchError((error) => of(RatiosActions.deleteRatioFailure({ error: error.message })))
          )
        )
      )
    );

    // Navigate after success
    this.ratioSuccess$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(
            RatiosActions.createRatioSuccess,
            RatiosActions.updateRatioSuccess,
            RatiosActions.deleteRatioSuccess
          ),
          tap(() => {
            this.store.select(selectSelectedStock).subscribe((stock) => {
              this.router.navigate(['/stocks', 'view', stock?.id, 'ratio']);
            });
          })
        ),
      { dispatch: false }
    );
  }
}
